import React, { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Chip from '@mui/material/Chip'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import LinearProgress from '@mui/material/LinearProgress'
import Alert from '@mui/material/Alert'
import { alpha, useTheme } from '@mui/material/styles'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import PauseCircle from '@mui/icons-material/PauseCircle'
import PlayCircleFilledOutlined from '@mui/icons-material/PlayCircleFilledOutlined'
import PauseCircleOutlineRounded from '@mui/icons-material/PauseCircleOutlineRounded'
import PlayCircleOutlineRounded from '@mui/icons-material/PlayCircleOutlineRounded'
import SwapHorizRounded from '@mui/icons-material/SwapHorizRounded'
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded'
import CloseRounded from '@mui/icons-material/CloseRounded'
import HourglassEmptyRounded from '@mui/icons-material/HourglassEmptyRounded'
import EventAvailableRounded from '@mui/icons-material/EventAvailableRounded'
import PlayCircleFilledRounded from '@mui/icons-material/PlayCircleFilledRounded'
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { systemValueCodeLabel, systemValueLabel } from '../../../generic/systemValues/systemValueLabel'
import { useAuthorization } from '../../../generic/hooks/useAuthorization'
import { useDateFormatter } from '../../../generic/hooks/useDateFormatter'
import CopyFabricationOrderDialog from './CopyFabricationOrderDialog'
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded'
import { orderAttentionReasons } from './orderAttention'
import {
  canTransitionFabricationOrderStatus,
  isFabricationOrderOperable
} from '../../../generic/utilities/statusPermissionGating'
import { isActiveJobsConflict, buildActiveJobsConflictMessage } from '../activeJobsConflictAlert'
import ForcedReturnDialog from '../ForcedReturnDialog'
import { useForcedReturnGate } from '../useForcedReturnGate'
import { SmartDialog } from '../../../generic/utilities/SmartDialog'
import createDialogTitle from '../../../generic/utilities/dialogUtil'
import QrCodeGeneratorDialog, { buildAvailableFields } from '../../../generic/qr/QrCodeGeneratorDialog'
import { deviationLabel } from './deviationLabel'
import { pauseStateLabel } from './pauseStateLabel'
import { progressLabel } from './progressLabel'
import { currentOperationLabel } from './currentOperationLabel'
import { changeStatus, fetchMainImageThumbnail, pauseOrderParts, resumeOrderParts, pausePart } from './detailApi'
import PausePartSelectEndJobDialog from '../../parts/pause/PausePartSelectEndJobDialog'
import PauseResumeFinishedList from '../../parts/pause/PauseResumeFinishedList'
import { groundColor, raisedColor } from './detailSurfaces'
import { CONTROL_RADIUS } from './detailControls'

// RN73/V-12/H5: the fixed 4-value FabricationOrderGlobalStatusEnum, minus the current status and
// minus PLANNED — PLANNED is never an interactive destination from ANY origin (S47).
const ALL_STATUSES = ['PENDING', 'PLANNED', 'LAUNCHED', 'CLOSED']

// Pasada 19 (defecto 6): each value of the fixed status enum gets its OWN icon+colour pair — never
// a single fixed colour with a round dot regardless of status, which reads PLANNED and an order in
// progress the same way. Colour is only ever a repeat of the icon/text, never the sole tell.
const STATUS_ICONS = { PENDING: HourglassEmptyRounded, PLANNED: EventAvailableRounded, LAUNCHED: PlayCircleFilledRounded, CLOSED: CheckCircleRounded }
const STATUS_COLOR_KEY = { PENDING: 'text', PLANNED: 'info', LAUNCHED: 'primary', CLOSED: 'success' }
// ⚠️⚠️ LA AUSENCIA DE UNA CIFRA SE DECLARA, Y `?? '—'` NO BASTA. La frontera (`detailApi.js`)
// normaliza las ausencias a CADENA VACÍA con `asEmptyString`, y `'' ?? x` devuelve `''`: el `??`
// está defusado y el hueco se pinta EN BLANCO. Un blanco no dice "no hay dato", no dice nada
// (RN8) — y al lado de un rótulo como «buenas» se lee como si el número se hubiera caído.
//
// 📌 Lo destapó el chip de canceladas: allí se escribió la comparación correcta y se vio que los
// otros cinco sitios de esta cabecera llevaban el `??` puesto sin efecto. Sus tests fijaban el
// texto en blanco, o sea que fijaban el defecto, que es por lo que sobrevivió.
//
// ⚠️ Un CERO no pasa por aquí: `0` es un valor presente y se pinta. Solo el hueco recibe guion.
const orDash = (value) => (value === '' || value === null || value === undefined ? '—' : value)

const statusColorOf = (theme, status) => {
  const colorKey = STATUS_COLOR_KEY[status] || 'text'
  return colorKey === 'text' ? theme.palette.text.secondary : theme.palette[colorKey].main
}

// Los campos que la ficha ofrece al generar un QR de la orden, en la forma `{field, name, type}`
// que espera `buildAvailableFields` — el mismo patrón que `FabricationOrder.jsx` con `getColumns()`.
//
// ⚠️ EL COMENTARIO QUE HABÍA AQUÍ ERA FALSO, y lo destapó el PO comparando las dos ventanas: decía
// "kept small on purpose: the detail's QR covers the same fields the listing already offers", y
// eran CINCO frente a los ~22 del listado. No se recortó a propósito: se afirmó una paridad que
// nunca existió, y por eso nadie la echó en falta hasta que alguien puso los dos diálogos al lado.
// Es la misma clase de comentario que ya costó un encargo de trabajo inventado en OperationsBlock:
// uno que afirma una CAPACIDAD no lo desmiente nada, porque comprobarlo exige ir a mirar otro sitio.
//
// S381: entran los NUEVE campos del listado que resuelven sobre el DTO del detalle HOY, sin pedirle
// nada a Sun. Los que faltan del listado (workflow.name, quantityOfPartsManufactured, minStartDate,
// parentFo, forecast, expirationDate, lineOrder.targetDate, plan, partReference.comment) resuelven
// a cadena vacía aquí: el DTO del detalle no los trae, y ofrecerlos vacíos sería peor que no
// ofrecerlos.
//
// ⚠️ CADA CAMPO LLEVA EL `type` QUE DECLARA EL LISTADO, y no es decoración: sin `type`,
// `formatQrValue` NO normaliza y una fecha viaja CRUDA al QR (`2026-03-18T00:00:00Z`) donde el
// listado manda `2026-03-18T00:00`. Ahí estaba una de las dos divergencias de contenido que vio el
// PO entre las dos ventanas, y añadir `plannedEndDate` sin su tipo la habría reintroducido por la
// puerta de atrás.
//
// ⚠️ `fabricationOrder` Y NO `code`: la CLAVE del QR sale del path del campo, así que el mismo dato
// viajaba como `CODE=` desde aquí y `FABRICATIONORDER=` desde el listado. El valor vive en
// `source.code` y se expone con el nombre del listado al construir la instancia (ver más abajo),
// que es lo que hace que el código impreso diga lo mismo se genere donde se genere.
// Historia 226 (S144, R7b): the titles are i18n keys, translated where the dialog is opened — the offered title is
// what gets printed on the label.
export const QR_COLUMNS = [
  { field: 'fabricationOrder', name: 'Fabrication Order' },
  { field: 'partReference.code', name: 'Part Reference' },
  { field: 'quantity.nominal', name: 'Quantity' },
  { field: 'totalQuantity', name: 'Total Quantity' },
  { field: 'status', name: 'Status' }, // system-value: raw-ok
  { field: 'statusProgress', name: 'Status progress' }, // system-value: raw-ok
  { field: 'targetDate', name: 'Target date', type: 'dateTimePicker' },
  { field: 'deliveryDate', name: 'Delivery date', type: 'dateTimePicker' },
  { field: 'plannedEndDate', name: 'Planned end date', type: 'dateTimePicker' },
  { field: 'priority', name: 'Priority' },
  { field: 'client.code', name: 'Client code' },
  { field: 'client.name', name: 'Client name' },
  { field: 'order.code', name: 'Order' },
  { field: 'lineOrder.lineNumber', name: 'Line' }
]

/**
 * La instancia que recibe el diálogo del QR.
 *
 * ⚠️ `fabricationOrder` es un ALIAS de `source.code`, no un campo del DTO. Existe para que la clave
 * del QR (que sale del PATH) coincida con la que emite el listado. Alternativas descartadas:
 * renombrar el campo del DTO tocaría toda la ficha por un detalle del QR, y cambiar `wireKey`
 * afectaría a las SEIS entidades que usan este diálogo.
 */
const qrInstanceOf = (source) => ({ ...source, id: source.fabricationOrderId, fabricationOrder: source.code })

// Pasada 17 (armazón visual, MIC-4252 §6.1): a labeled cell of the five-indicator situation panel.
// Purely presentational — no data, no testids of its own; every field it wraps keeps its own.
const IndicatorCell = ({ label, first = false, children }) => (
  <Box
    sx={(theme) => ({
      px: { xs: 1.5, md: 2 },
      minWidth: 0,
      borderLeft: first ? 'none' : `1px solid ${alpha(theme.palette.text.primary, 0.08)}`
    })}
  >
    <Typography
      variant='caption'
      sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', mb: 0.5 }}
    >
      {label}
    </Typography>
    {children}
  </Box>
)

// Identity + situation panel + frequent actions of the fabrication order detail (CU-L1/CU-W1..
// W10, plan-jupiter.md §1). T1b wires the real mutations behind Change status/Pause/Resume/Copy/
// Generate QR/Modify quantity (T1b/T2 keep the actual General edit batch out of scope; this is
// only the header's own actions). Pasada 17 lays out the two identity/situation levels over the
// theme (§6.1) — no logic below this comment changed, only the JSX/sx around it.
// One cell of the QUANTITY breakdown: dot, value and word on one line. The text stays
// "<value> <word>" (the space is real) so the cell reads the same as the chips it replaces.
const QuantityBreakdownItem = ({ testId, value, label, dotColor, color = 'text.primary' }) => (
  <Stack data-testid={testId} direction='row' spacing={0.75} alignItems='center' sx={{ color, whiteSpace: 'nowrap' }}>
    <Box component='span' sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: dotColor, flexShrink: 0 }} />
    <Typography component='span' variant='body2' sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{orDash(value)}</Typography>
    {' '}
    <Typography component='span' variant='caption' sx={{ color: color === 'text.primary' ? 'text.secondary' : color }}>{label}</Typography>
  </Stack>
)

const DetailHeader = ({
  header,
  sheet = {},
  // With the sheet served by N1, `sheet` has no full row to copy: "Copy as" asks for it when it opens
  // (`onCopyRequested`) and the copy dialog waits for `copySheet` (`copyStatus` says how the read goes).
  lazyCopy = false,
  copySheet = {},
  copyStatus = 'ready',
  onCopyRequested = () => {},
  fabricationOrderId,
  onNavigateToParent = () => {},
  onOpenModifyQuantity = () => {},
  onOpenBalanced = () => {},
  // H179 (RN5): una OF sin workflow no tiene nada que balancear.
  offersBalanced = true,
  onAfterStatusChange = () => {},
  onAfterPauseResume = () => {},
  onNotFound = () => {},
  onClose = () => {},
  showAlert = () => {}
}) => {
  const { t_ } = useSmartTranslation()
  const isUserAuthorized = useAuthorization()
  const { formatDate } = useDateFormatter()
  const theme = useTheme()
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null)
  const [showQr, setShowQr] = useState(false)
  const [copyOpen, setCopyOpen] = useState(false)
  // S307/RN74.2: pausing/resuming asks for confirmation before invoking any route — parity with
  // the listing, which already confirms before calling order/pause|resume/{id}.
  const [pauseConfirmAction, setPauseConfirmAction] = useState(null)
  // Task 21: the listing's follow-up dialogs after order/pause|resume/{id} — the FINISHED parts it
  // ignored, and the parts on a device that still need the "pause now / at end of job" choice.
  const [finishedParts, setFinishedParts] = useState([])
  const [partsOnDevicesToPause, setPartsOnDevicesToPause] = useState([])
  const [partPausesInFlight, setPartPausesInFlight] = useState(0)
  // Synchronous twin of partPausesInFlight: a second click can arrive before the re-render.
  const partPausePendingRef = useRef(false)
  const [awaitingFollowUps, setAwaitingFollowUps] = useState(false)
  const forcedReturnGate = useForcedReturnGate()

  const source = header || {}
  const deviation = deviationLabel(source.deviation)
  const pauseLabel = pauseStateLabel(source)
  const progress = progressLabel(source)
  const operationLabel = currentOperationLabel(source)

  // La foto de la cabecera: el MISMO endpoint en lote que el listado de OFs usa para sus filas
  // (`useMainImageResolution` -> `partReferences/mainImages/thumbnails`), pedido aquí para una sola
  // referencia. No se reutiliza el hook entero a propósito: resuelve DOS endpoints —metadatos y
  // miniaturas— y la cabecera solo necesita los bytes, así que el hook costaría una petición de más
  // por apertura. Lo que sí se reutiliza es el contrato: base64 JPEG sin prefijo, por identificador.
  // Fallo de red o respuesta vacía dejan la miniatura vacía y la cabecera dice "Sin foto".
  const [mainImageThumbnail, setMainImageThumbnail] = useState('')
  const [mainImageFailed, setMainImageFailed] = useState(false)
  const partReferenceId = source.partReference?.id

  useEffect(() => {
    let alive = true
    setMainImageFailed(false)
    setMainImageThumbnail('')
    if (!partReferenceId) {
      return undefined
    }
    fetchMainImageThumbnail(partReferenceId)
      .then((thumbnail) => {
        if (alive) setMainImageThumbnail(thumbnail)
      })
      .catch(() => {
        if (alive) setMainImageThumbnail('')
      })
    return () => { alive = false }
  }, [partReferenceId])

  const hasMainImage = !!mainImageThumbnail && !mainImageFailed

  const parentOrder = source.parentFabricationOrder || {}
  // Se comprueba lo que se va a PINTAR (`code`), no lo que hay al lado (`id`). Por contrato el
  // objeto trae los dos o es `null`, así que hoy da igual — pero comprobar un campo e imprimir otro
  // es exactamente la forma del defecto de "null - null", y no se deja puesta a propósito.
  // El `id` sigue haciendo falta: es lo que navega al abrir la OF superior.
  const hasParentOrder = !!parentOrder.id && !!parentOrder.code

  // D3 (a), fijado por S32: el indicador muestra `deliveryDate` y, si falta, cae a `targetDate` —
  // y el retraso se evalúa SIEMPRE contra `targetDate`. Eso no se toca.
  //
  // Defecto 1 (pasada 33, opción (a) del hub): lo que sí se toca es el RÓTULO. Presentar la fecha
  // objetivo bajo la palabra "Entrega" induce a error sobre un compromiso con el cliente: el dato
  // era correcto y el nombre mentía. Cuando se cae al objetivo, el rótulo lo dice. El dato mostrado
  // es el mismo de siempre, así que S32 sigue fijando lo que fijaba.
  const usesTargetDateAsDelivery = !source.deliveryDate && !!source.targetDate
  const displayedDeliveryDate = source.deliveryDate || source.targetDate
  const deliveryIndicatorLabel = usesTargetDateAsDelivery ? t_('Target date') : t_('Delivery')
  // Defecto 2 (pasada 33): adelanto, retraso o justo en plazo, elegido por el signo. La cifra se
  // muestra en valor absoluto en los dos casos con signo — el literal ya dice de qué lado está.
  const deviationDays = Number(deviation.days)
  const deviationIsNumber = deviation.computable && !Number.isNaN(deviationDays)
  const deviationText = !deviationIsNumber
    ? ''
    : deviationDays > 0
      ? t_('Late by {{days}} days', { days: deviationDays })
      : deviationDays < 0
        ? t_('Ahead by {{days}} days', { days: Math.abs(deviationDays) })
        : t_('On the target date')
  const deviationTone = !deviationIsNumber || deviationDays > 0 ? 'warning.main' : 'success.main'

  const operable = isFabricationOrderOperable(source.status)

  const quantity = source.quantity || {}
  const supportLine = [
    source.client?.name || '',
    source.order?.code ? `${t_('Order')} ${source.order.code}${source.lineOrder?.lineNumber ? ` · ${t_('Line')} ${source.lineOrder.lineNumber}` : ''}` : ''
  ].filter(Boolean)

  const deliverySupportLine = [
    source.priority !== null && source.priority !== undefined && source.priority !== '' ? `${t_('Priority')} ${source.priority}` : '',
    source.planType?.code ? `${t_('Plan type')} ${systemValueCodeLabel(t_, 'fabrication_order_plan_type', source.planType.code)}` : ''
  ].filter(Boolean).join(' · ')

  // S47/S50/S303: destinations offered from the current status — current status excluded, PLANNED
  // never offered, both ends of the transition must be authorized (RN73). Never removed from the
  // list for a missing permission (S50 keeps "PENDING" while dropping "CLOSED"), only skipped.
  const destinations = ALL_STATUSES
    .filter((status) => status !== source.status)
    .filter((status) => status !== 'PLANNED')
    .filter((status) => canTransitionFabricationOrderStatus(isUserAuthorized, source.status, status))

  const runStatusChange = async (newStatus) => {
    const runAction = () => changeStatus(fabricationOrderId, newStatus)
    try {
      await (newStatus === 'CLOSED'
        ? forcedReturnGate.runGuarded(runAction, fabricationOrderId)
        : runAction())
      onAfterStatusChange()
    } catch (error) {
      if (error?.status === 404) {
        onNotFound()
        return
      }
      // Same message as the legacy window for the active-jobs rejection (it lists the devices);
      // every other literal is still shown verbatim (E5/E6, §C.3).
      showAlert({
        severity: 'error',
        title: t_('Error changing status:'),
        message: isActiveJobsConflict(error) ? buildActiveJobsConflictMessage(t_, error.data) : (error?.message || '')
      })
    }
  }

  const handleForcedReturnConfirm = (choices) => forcedReturnGate.onConfirm(choices)
  const handleForcedReturnCancel = () => forcedReturnGate.onCancel()

  const handlePauseResumeAccept = () => {
    const action = pauseConfirmAction
    setPauseConfirmAction(null)
    runPauseResume(action)
  }

  const handlePauseResumeCancel = () => setPauseConfirmAction(null)

  // Task 21: same flow as the listing's handleConfirmationAccept — the legacy notice, and the parts
  // N14/N15 hand back (the same list order/pause|resume/{id} returns) split into the two dialogs.
  const runPauseResume = async (action) => {
    try {
      const parts = action === 'pause' ? await pauseOrderParts(fabricationOrderId) : await resumeOrderParts(fabricationOrderId)
      showAlert({
        severity: 'success',
        title: action === 'pause' ? t_('Part(s) has been paused') : t_('Part(s) has been resumed'),
        message: ''
      })
      if (action === 'pause') {
        setFinishedParts(parts.filter((part) => part.status === 'FINISHED'))
        setPartsOnDevicesToPause(parts.filter((part) => part.status !== 'FINISHED'))
      } else {
        setFinishedParts(parts)
      }
      setAwaitingFollowUps(true)
    } catch (error) {
      if (error?.status === 404) {
        onNotFound()
        return
      }
      showAlert({ severity: 'error', title: t_('Error changing status:'), message: error?.message || '' })
    }
  }

  // The header is re-read only once every follow-up dialog is answered and every per-part pause has
  // come back. The re-read runs in the background and no longer unmounts this header; the wait stays
  // so the refresh reads the order AFTER every per-part answer, not halfway through them.
  useEffect(() => {
    if (awaitingFollowUps && finishedParts.length === 0 && partsOnDevicesToPause.length === 0 && partPausesInFlight === 0) {
      setAwaitingFollowUps(false)
      onAfterPauseResume()
    }
  }, [awaitingFollowUps, finishedParts, partsOnDevicesToPause, partPausesInFlight, onAfterPauseResume])

  const skipPartOnDevice = () => setPartsOnDevicesToPause((queue) => queue.slice(1))

  // The listing's onPauseWorkflowForce: drop the part from the queue, then part/pause/{id}/{endJob}.
  // While a part/pause call is pending the choice is disabled (not offered, clicks ignored), so a
  // double click can neither pause the same part twice nor answer for the next part in the queue.
  const pausePartOnDevice = async (endJob) => {
    if (partPausePendingRef.current) return
    partPausePendingRef.current = true
    const partId = partsOnDevicesToPause[0].id
    skipPartOnDevice()
    setPartPausesInFlight((count) => count + 1)
    try {
      await pausePart(partId, endJob)
    } catch (error) {
      showAlert({ severity: 'error', title: t_('Error changing status:'), message: error?.message || '' })
    } finally {
      partPausePendingRef.current = false
      setPartPausesInFlight((count) => count - 1)
    }
  }

  // The copy form lives in CopyFabricationOrderDialog (legacy row-action logic).
  const handleCopy = () => setCopyOpen(true)

  // ADDENDUM 33 (E39) 2: dejan de ser píldoras. El NOMBRE cambia con ellos — `actionButtonSx`
  // apuntando a un radio que ya no es de píldora sería otro comentario que miente, y este
  // fichero lleva la semana entera pagando esos.
  const actionButtonSx = {
    borderRadius: CONTROL_RADIUS,
    textTransform: 'none',
    fontWeight: 700,
    px: 2
  }

  // Los motivos de atención. Al pasar la franja a píldora ya no se redacta ninguna frase con
  // ellos: lo único que se mira es SI hay alguno.
  //
  // ⚠️ La redacción de los motivos SE HA BORRADO, no se ha dejado "por si acaso": una función que
  // arma una frase que nadie pinta hace creer que la frase está en pantalla en algún sitio, y
  // comprobar que no exige ir a mirar. Si vuelve a hacer falta, está en el historial de este
  // fichero, que es donde debe estar.
  const operacionesEnCurso = Number(source.activeOperationCount) || 0
  // PO decision (#3120): the pill rises only for a late order or running operations nobody started
  // (no date). One or several running operations WITH a date, on time, are the normal state.
  const requiresAttention = orderAttentionReasons(source).some((reason) => reason.key === 'days-late' || reason.key === 'active-operations-undated')

  return (
    <Box
      data-testid='detail-header'
      sx={{
        flexShrink: 0,
        backgroundColor: groundColor(theme),
        px: { xs: 2, md: 4 },
        pt: 3
      }}
    >
      {!operable && (
        <Alert severity='info' data-testid='closed-order-banner' sx={{ mb: 2 }}>
          {t_('This order is closed')}
        </Alert>
      )}

      <CopyFabricationOrderDialog open={copyOpen} sheet={lazyCopy ? copySheet : sheet} sheetStatus={lazyCopy ? copyStatus : 'ready'} onClose={() => setCopyOpen(false)} showAlert={showAlert} />

      {/* Identity level — reference image, code/part reference, client/order/parent line, actions. */}
      <Stack direction='row' spacing={2} alignItems='stretch' sx={{ pb: 2.5 }}>
        {/* S442 (D13, plan del 23/09) — AQUÍ HABÍA UNA BARRA DE COLOR del estado a lo alto de la
            cabecera, y se quitó. Antes de quitarla se comprobó que no era lo único que lo decía: el
            estado se lee entero, con su palabra y su icono, en la píldora de abajo. */}
        {/* S445 (ADDENDUM 30 / E36 D) — el hueco de la foto SOLO existe cuando hay foto.
            Ocupaba un sitio de primera —80px en la esquina superior izquierda, antes que el código
            de la orden— para decir "Sin foto", y lo decía en TODAS las órdenes abiertas en tres días
            y en las dos instalaciones. Un hueco permanente para anunciar una ausencia no informa de
            nada: que no haya foto ya se ve por que no hay foto.
            Sin ella, ese ancho se lo quedan el código y la referencia, que es lo que se viene a leer.

            ⚠️ Compensación asumida y medida, no pasada por alto: la miniatura llega en una segunda
            petición, así que en una orden CON foto el cuadro aparece cuando llega y el texto se
            desplaza ~96px una vez. Se elige así —y no reservando el hueco mientras carga— porque
            reservarlo devolvería el parpadeo a la mayoría: el cuadro aparecería y desaparecería en
            todas las órdenes sin foto, que son casi todas. Un salto único en la minoría es mejor que
            un parpadeo en la mayoría.
            ⚠️ Y la condición sigue siendo "hay BYTES", no "hay UUID": con el UUID, un fallo de carga
            dejaba un hueco mudo. Ahora simplemente no hay hueco. */}
        {hasMainImage && (
          <Box
            data-testid='fo-main-image-slot'
            sx={{
              width: 80,
              height: 80,
              flexShrink: 0,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
              backgroundColor: raisedColor(theme),
              overflow: 'hidden'
            }}
          >
            {/* ⚠ Defecto encontrado por el PO al poner la primera foto de demo: esto era un
                `<img src>` apuntando a `document/file/download/{uuid}`, y **nunca pudo cargar**.
                Daba 401 siempre. El comentario de antes decía que era "la MISMA ruta de descarga
                que ya usa el bloque de Documentos", y esa media verdad es la que lo hizo parecer
                verificado: la ruta es la misma, pero el MECANISMO no. `apiDownloadFile` lleva la
                cabecera `Authorization`; una petición de imagen que dispara el navegador por un
                `src` no puede llevarla. Nadie lo vio porque NINGUNA orden de ningún entorno tenía
                foto: el camino no se ejecutaba jamás.
                Se resuelve con `partReferences/mainImages/thumbnails` —base64 por identificador—,
                que es exactamente lo que ya hace el LISTADO de OFs para sus filas: misma llamada,
                así que las dos pantallas enseñan la misma imagen por construcción. */}
            <Box
              component='img'
              data-testid='fo-main-image'
              alt={t_('Fabrication order')}
              src={'data:image/jpeg;base64,' + mainImageThumbnail}
              onError={() => setMainImageFailed(true)}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
        )}

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant='overline' sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, letterSpacing: 1.5 }}>
            {t_('Fabrication order')}
          </Typography>
          <Stack direction='row' spacing={1.5} alignItems='baseline' flexWrap='wrap' useFlexGap>
            <Typography id='fo-detail-code' variant='h6' sx={{ fontWeight: 800, fontSize: 32, lineHeight: 1.15 }}>{source.code}</Typography>
            {!!source.partReference?.code && (
              <Typography variant='subtitle1' sx={{ fontWeight: 600, color: 'text.secondary' }}>{source.partReference.code}</Typography>
            )}
            {!!source.partReference?.name && (
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>{source.partReference.name}</Typography>
            )}
            {/* ⚠️⚠️ «REQUIERE ATENCIÓN» ES UNA PÍLDORA JUNTO AL TÍTULO, Y SOLO DICE ESO.
                Antes era una banda a ancho completo con la frase entera —"83 días de retraso ·
                avance 0 % · 30 operaciones en curso, la más antigua desde el 30/06/2026"—. El PO la
                cambia por la píldora porque, dicho con sus palabras, «es información que ya puedes
                ver debajo»: el retraso está en la tarjeta de Desviación y el avance en la de
                Avance, a dos dedos de aquí.

                📌 SE PIERDE UN DATO Y ESTÁ DECIDIDO ASÍ, que es lo contraintuitivo y por lo que
                esta nota existe: «la más antigua desde el 30/06/2026» NO está en ningún otro sitio
                de la ficha. Sale de `currentOperationStartedAt`, un campo que Sun añadió a N1 esta
                misma mañana (S409) esquivando tres trampas, y el PO decide SABIÉNDOLO que deje de
                pintarse. El campo sigue viajando y sigue siendo correcto: simplemente no se
                enseña. Quien lo vea en el DTO y no en la pantalla no está ante un olvido.

                ⚠️ RN35: la píldora conserva icono Y literal. Lo que se va es la frase larga, no la
                forma de decir el estado — un ámbar a secas no lo diría.

                ⚠️ Y `orderAttentionReasons` se queda ENTERA aunque su texto ya no se pinte: sigue
                siendo quien decide SI hay aviso. La píldora sale con el retraso o con la operación en
                curso sin fecha; las operaciones en curso con fecha NO la levantan solas (decisión
                del PO, #3120). Simplificarla porque «ya no se lee» apagaría esos casos en silencio. */}
            {requiresAttention && (
              <Chip
                data-testid='header-attention'
                size='small'
                icon={<WarningAmberRounded sx={{ fontSize: '16px !important', color: 'warning.main !important' }} />}
                label={t_('Requires attention')}
                sx={(muiTheme) => ({
                  fontWeight: 700,
                  color: muiTheme.palette.warning.main,
                  backgroundColor: alpha(muiTheme.palette.warning.main, 0.12),
                  border: `1px solid ${alpha(muiTheme.palette.warning.main, 0.35)}`
                })}
              />
            )}
          </Stack>
          <Stack direction='row' spacing={1.5} alignItems='center' flexWrap='wrap' useFlexGap sx={{ mt: 0.5 }}>
            {supportLine.map((piece) => (
              <Typography key={piece} variant='body2' sx={{ color: 'text.secondary' }}>{piece}</Typography>
            ))}
            {hasParentOrder && (
              <Button
                data-testid='parent-order-link'
                onClick={() => onNavigateToParent(parentOrder.id)}
                size='small'
                startIcon={<ArrowUpwardRounded fontSize='small' />}
                sx={{
                  borderRadius: CONTROL_RADIUS,
                  border: `1px solid ${alpha(theme.palette.text.primary, 0.24)}`,
                  color: 'text.primary',
                  textTransform: 'none',
                  minWidth: 0,
                  px: 1.5
                }}
              >
                {`${t_('Parent order')}: ${parentOrder.code}`}
              </Button>
            )}
          </Stack>
        </Box>

        <Stack direction='row' spacing={1} alignItems='center' data-testid='header-actions' sx={{ flexShrink: 0 }}>
          {isUserAuthorized('FABRICATION_ORDER_STATUS_CHANGE') && destinations.length > 0 && (
            <>
              <Button
                data-testid='change-status-button'
                onClick={(event) => setStatusMenuAnchor(event.currentTarget)}
                variant='contained'
                color='primary'
                size='small'
                startIcon={<SwapHorizRounded fontSize='small' />}
                sx={actionButtonSx}
              >
                {t_('Change status')}
              </Button>
              <Menu anchorEl={statusMenuAnchor} open={!!statusMenuAnchor} onClose={() => setStatusMenuAnchor(null)}>
                {destinations.map((status) => (
                  <MenuItem
                    key={status}
                    data-testid={`status-destination-${status}`}
                    onClick={() => { setStatusMenuAnchor(null); runStatusChange(status) }}
                  >
                    {t_(status)}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          {source.offersPause && operable && isUserAuthorized('PAUSE_FO_FABRICATION_ORDERS') && (
            <Button
              data-testid='pause-button'
              onClick={() => setPauseConfirmAction('pause')}
              variant='outlined'
              size='small'
              startIcon={<PauseCircleOutlineRounded fontSize='small' />}
              sx={actionButtonSx}
            >
              {t_('Pause FO')}
            </Button>
          )}
          {source.offersResume && operable && isUserAuthorized('RESUME_FO_FABRICATION_ORDERS') && (
            <Button
              data-testid='resume-button'
              onClick={() => setPauseConfirmAction('resume')}
              variant='outlined'
              size='small'
              startIcon={<PlayCircleOutlineRounded fontSize='small' />}
              sx={actionButtonSx}
            >
              {t_('Resume FO')}
            </Button>
          )}
          {!source.offersPause && !source.offersResume && operable && isUserAuthorized('PAUSE_FO_FABRICATION_ORDERS') && (
            <Button
              data-testid='pause-button'
              disabled
              title={t_('This order has no active parts to pause')}
              variant='outlined'
              size='small'
              startIcon={<PauseCircleOutlineRounded fontSize='small' />}
              sx={actionButtonSx}
            >
              {t_('Pause FO')}
            </Button>
          )}
          {source.qrAvailable && (
            <IconButton
              aria-label={t_('Generate QR')}
              title={t_('Generate QR')}
              onClick={() => setShowQr(true)}
              sx={{ border: `1px solid ${alpha(theme.palette.text.primary, 0.16)}`, borderRadius: CONTROL_RADIUS }}
            >
              <QrCode2Icon fontSize='small' />
            </IconButton>
          )}
          <IconButton
            aria-label={t_('More options')}
            title={t_('More options')}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            sx={{ border: `1px solid ${alpha(theme.palette.text.primary, 0.16)}`, borderRadius: CONTROL_RADIUS }}
          >
            <MoreVertIcon fontSize='small' />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
            {isUserAuthorized('COPY_FABRICATION_ORDERS') && (
              <MenuItem disabled={!lazyCopy && !sheet.id} onClick={() => { setMenuAnchor(null); if (lazyCopy) onCopyRequested(); handleCopy() }}>{t_('Copy as')}</MenuItem>
            )}
            {isUserAuthorized('WORKFLOWS_TAB') && offersBalanced && (
              <MenuItem onClick={() => { setMenuAnchor(null); onOpenBalanced() }}>{t_('Balanced')}</MenuItem>
            )}
            {isUserAuthorized('FABRICATION ORDERS') && operable && (
              <MenuItem onClick={() => { setMenuAnchor(null); onOpenModifyQuantity() }}>{t_('Modify quantity')}</MenuItem>
            )}
            {source.qrAvailable && (
              <MenuItem onClick={() => { setMenuAnchor(null); setShowQr(true) }}>{t_('Generate QR')}</MenuItem>
            )}
          </Menu>
          <IconButton
            aria-label={t_('Close')}
            title={t_('Close')}
            onClick={onClose}
            sx={{ border: `1px solid ${alpha(theme.palette.text.primary, 0.16)}`, borderRadius: CONTROL_RADIUS }}
          >
            <CloseRounded fontSize='small' />
          </IconButton>
        </Stack>
      </Stack>

      {/* Situation level — five indicators, RN17/§6.1 (this row never scrolls with the block). */}
      <Box
        sx={(muiTheme) => ({
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(6, 1fr)' },
          rowGap: 2,
          borderTop: `1px solid ${alpha(muiTheme.palette.text.primary, 0.08)}`,
          borderBottom: `1px solid ${alpha(muiTheme.palette.text.primary, 0.08)}`,
          mx: { xs: -2, md: -4 },
          px: { xs: 2, md: 4 },
          py: 2.5
        })}
      >
        <IndicatorCell label={t_('Status')} first>
          {(() => {
            const StatusIcon = STATUS_ICONS[source.status]
            const statusColor = statusColorOf(theme, source.status)
            return (
              <Chip
                data-testid='header-status-chip'
                size='small'
                icon={StatusIcon ? <StatusIcon data-testid={`header-status-icon-${source.status}`} sx={{ fontSize: '16px !important', color: `${statusColor} !important` }} /> : undefined}
                label={systemValueLabel(t_, 'FabricationOrderGlobalStatusEnum', source.status || '')}
                sx={{
                  fontWeight: 800,
                  backgroundColor: alpha(statusColor, 0.16),
                  color: statusColor,
                  border: `1px solid ${alpha(statusColor, 0.35)}`
                }}
              />
            )
          })()}
          <Box data-testid='current-operation-indicator' sx={{ mt: 0.75 }}>
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
              {operationLabel.operationText || t_(operationLabel.key, operationLabel.values)}
            </Typography>
          </Box>
          {pauseLabel.key && (
            <Box data-testid='pause-indicator' sx={{ mt: 0.5 }}>
              <Typography variant='caption' sx={{ color: 'warning.main', fontWeight: 700 }}>
                {t_(pauseLabel.key, pauseLabel.values)}
              </Typography>
            </Box>
          )}
        </IndicatorCell>

        <IndicatorCell label={t_('Progress')}>
          <Box data-testid='progress-indicator'>
            {progress.computable
              ? (
                <>
                  <Stack direction='row' spacing={1.5} alignItems='center'>
                    <Typography sx={{ fontSize: 26, fontWeight: 800 }}>{`${progress.percent}%`}</Typography>
                    <LinearProgress
                      variant='determinate'
                      value={progress.percent}
                      sx={{
                        flexGrow: 1,
                        height: 8,
                        borderRadius: 999,
                        backgroundColor: alpha(theme.palette.text.primary, 0.12),
                        '& .MuiLinearProgress-bar': { borderRadius: 999, backgroundColor: theme.palette.primary.main }
                      }}
                    />
                  </Stack>
                  {/* `good` y `nominal` son number|null: sin esto, la línea de apoyo del progreso
                      podía decir "null / null" bajo un porcentaje perfectamente calculado. El mismo
                      `?? '—'` que ya usaba la tarjeta de Cantidad tres líneas más abajo. */}
                  <Typography variant='caption' sx={{ color: 'text.secondary' }}>{`${orDash(progress.good)} / ${orDash(progress.nominal)}`}</Typography>
                </>
                )
              : <Typography variant='body2' sx={{ color: 'text.secondary' }}>{t_('Progress not computable')}</Typography>}
          </Box>
        </IndicatorCell>

        <IndicatorCell label={t_('Quantity')}>
          <Box data-testid='quantity-indicator'>
            <Typography sx={{ fontSize: 26, fontWeight: 800 }}>
              {/* ⚠️⚠️ ERA `totalQuantity ?? quantity.nominal` Y ESE `??` ERA EL DEFECTO. No son dos
                  lecturas del mismo número entre las que se pueda elegir: `quantity.nominal` es
                  `fo.quantity` —para cuántas unidades es la orden— y `totalQuantity` es un
                  recuento de FILAS DE PIEZA que INCLUYE LAS CANCELADAS. Con 20 pedidas y 10
                  canceladas salía «0 / 30» al lado de «20 Pending»: dos denominadores mezclados
                  en la misma línea, y ninguno de los dos mal por separado.
                  Manda el NOMINAL, que es el que cierra con los pendientes y el que ya usa el
                  indicador de avance de al lado (S285). */}
              {`${orDash(quantity.good)} / ${orDash(quantity.nominal)}`}
            </Typography>
            {/* ⚠ ADDENDUM 30 A: este "Pendiente" es el ÚNICO de la ficha que sigue diciendo
                "Pendiente", y por eso tiene clave propia (`Quantity pending`). Aquí la palabra no
                habla de un dato que falte ni de un estado: rotula una CANTIDAD real, la que queda
                por fabricar. Los huecos dicen "Sin informar" y las imposibilidades "No calculable". */}
            {/* The breakdown in a FIXED 2x2 grid instead of chips that wrapped wherever they fit.
                Row 1 is what makes up the quotient above (good + pending = nominal); row 2 is
                informative and never adds up (S412: cancelled is historical and may exceed the
                nominal). Rejected is tinted ONLY when there is some (addendum 33 point 1), and zeros
                are shown: a zero is a present value, the dash (`orDash`) is the absence (§2.2, RN8). */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, max-content)', columnGap: 2, rowGap: 0.5, mt: 0.75 }}>
              <QuantityBreakdownItem testId='quantity-good' value={quantity.good} label={t_('good')} dotColor={theme.palette.success.main} />
              <QuantityBreakdownItem testId='quantity-pending' value={quantity.pending} label={t_('Quantity pending')} dotColor={theme.palette.info.main} color='info.main' />
              <QuantityBreakdownItem
                testId='quantity-rejected'
                value={quantity.rejected}
                label={t_('rejected')}
                dotColor={Number(quantity.rejected) >= 1 ? theme.palette.error.main : theme.palette.text.disabled}
                color={Number(quantity.rejected) >= 1 ? 'error.main' : 'text.primary'}
              />
              <QuantityBreakdownItem testId='quantity-cancelled' value={quantity.cancelled} label={t_('cancelled')} dotColor={theme.palette.text.disabled} />
            </Box>
          </Box>
        </IndicatorCell>

        <IndicatorCell label={deliveryIndicatorLabel}>
          <Box data-testid='delivery-indicator' data-shows-target-date={usesTargetDateAsDelivery ? 'true' : 'false'}>
            <Typography sx={{ fontSize: 22, fontWeight: 800 }}>{orDash(formatDate(displayedDeliveryDate) || displayedDeliveryDate)}</Typography>
            {source.lateAgainstTargetDate && (
              <Typography data-testid='delayed-badge' variant='caption' sx={{ color: 'error.main', fontWeight: 700, display: 'block' }}>
                {t_('Delayed')}
              </Typography>
            )}
            {!!deliverySupportLine && (
              <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block' }}>{deliverySupportLine}</Typography>
            )}
          </Box>
        </IndicatorCell>

        {/* §5.3 — TARJETA NUEVA. `activeOperationCount` ya viajaba y solo se leía dentro del
            subtítulo del estado: aquí responde por sí misma a "¿cuánto hay moviéndose?".
            ⚠ El código de la operación en curso se enseña SOLO cuando hay una: `currentOperation`
            únicamente viaja con EXACTAMENTE UNA en curso (S281), así que con varias no hay una
            que nombrar y escribir cualquiera sería elegir por el lector. */}
        <IndicatorCell label={t_('Operations')}>
          <Box data-testid='operations-indicator'>
            <Typography sx={{ fontSize: 26, fontWeight: 800, color: operacionesEnCurso > 0 ? 'info.main' : 'text.primary' }}>
              {operacionesEnCurso}
            </Typography>
            <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block' }}>
              {t_('in progress in the order')}
            </Typography>
            {operacionesEnCurso === 1 && !!source.currentOperation?.code && (
              <Typography data-testid='operations-current' variant='caption' sx={{ color: 'text.secondary', display: 'block' }}>
                {source.currentOperation.code}
              </Typography>
            )}
          </Box>
        </IndicatorCell>

        <IndicatorCell label={t_('Deviation')}>
          <Box data-testid='deviation-indicator'>
            {deviation.computable
              ? (
                <>
                  {/* Revisión del PO (pasada 33, defecto 2): este literal era siempre "Late by
                      {{days}} days" y dejaba que el SIGNO cargara con el significado, así que una
                      desviación negativa se leía como "Late by -77 days" — el texto decía lo
                      contrario de lo que pasaba. Ahora el signo elige el literal y la cifra viaja
                      SIEMPRE en valor absoluto. El caso 0 tiene el suyo: ni adelanto ni retraso.
                      ⚠ Esto NO toca `deviationLabel`, que distingue "no computable" de 0 (P-3): un
                      0 real sigue siendo un valor presente, no una ausencia. */}
                  <Typography sx={{ fontSize: 18, fontWeight: 800, color: deviationTone }}>
                    {deviationText}
                  </Typography>
                  {deviation.basisDate && (
                    <Typography data-testid='deviation-basis' variant='caption' sx={{ color: 'text.secondary' }}>
                      {/* ENMIENDA del addendum 10: `basisDate` lleva el MISMO valor que `targetDate`
                          y ahora llega como instante. Se pinta por el mismo `formatDate` que el
                          indicador de arriba — si las dos salieran de sitios distintos, la tarjeta
                          podría decir "objetivo 31" y debajo "respecto al 30", que es el defecto
                          que el addendum existe para quitar. */}
                      {t_('As of {{date}}', { date: formatDate(deviation.basisDate) || deviation.basisDate })}
                    </Typography>
                  )}
                </>
                )
              : <Typography variant='body2' sx={{ color: 'text.secondary' }}>{t_('Deviation not computable')}</Typography>}
          </Box>
        </IndicatorCell>
      </Box>

      {showQr && (
        <QrCodeGeneratorDialog
          open={showQr}
          onClose={() => setShowQr(false)}
          entityType='FabricationOrder'
          entityLabel={t_('Fabrication Order')}
          instance={qrInstanceOf(source)}
          subtitle={source.code}
          availableFields={buildAvailableFields(QR_COLUMNS.map(c => ({ ...c, name: t_(c.name) })), qrInstanceOf(source))}
        />
      )}

      <SmartDialog
        setOpen={!!pauseConfirmAction}
        message={t_(`Are you sure you want to ${pauseConfirmAction} the fabrication order?`)}
        title={createDialogTitle({
          title: t_('Confirm'),
          icon: pauseConfirmAction === 'pause' ? PauseCircle : PlayCircleFilledOutlined
        })}
        accept
        cancel
        close={false}
        acceptCallback={handlePauseResumeAccept}
        cancelCallback={handlePauseResumeCancel}
      />

      {/* Task 21: mounted with the same props as the listing (FabricationOrder.jsx) — the finished
          list first, then one end-of-job choice per part on a device, held back while the previous
          part's pause is still pending. */}
      <PausePartSelectEndJobDialog
        setOpen={finishedParts.length === 0 && partsOnDevicesToPause.length > 0 && partPausesInFlight === 0 ? partsOnDevicesToPause[0].id : false}
        cancelCallback={skipPartOnDevice}
        part={partsOnDevicesToPause.length > 0 ? partsOnDevicesToPause[0] : {}}
        pauseNowCallback={() => { pausePartOnDevice(false) }}
        pauseAtJobEndCallback={() => { pausePartOnDevice(true) }}
      />
      <PauseResumeFinishedList
        setOpen={finishedParts.length > 0}
        parts={finishedParts}
        closeCallback={() => setFinishedParts([])}
      />

      <ForcedReturnDialog
        open={forcedReturnGate.dialog.open}
        jobs={forcedReturnGate.dialog.jobs}
        changed={forcedReturnGate.dialog.changed}
        submitting={forcedReturnGate.dialog.submitting}
        onConfirm={handleForcedReturnConfirm}
        onCancel={handleForcedReturnCancel}
      />
    </Box>
  )
}

export default DetailHeader
